import React, { useState } from "react";
import { Grid, Box, SvgIcon, InputAdornment } from "@mui/material";
// @ts-ignore
import {
    StriimLink,
    StriimInputField,
    StriimTypography,
    StriimFeedbackBlock,
    StriimButton,
    StriimTabs,
    StriimTab
} from "@striim/striim-ui";

import Expand from "/app/images/striimline/expand.svg";
import { styles } from "./selectTables.styles";
import { useSelectTables } from "./select-tables-context";
import { Minimize, Close, Retry } from "src/generic/icon/customIcons";
import FullScreenInputModal from "./full-screen-input-modal";
import { NewTab } from "src/generic/icon/customIcons";
import { tablesInputHelperText } from "./select-tables-modal/select-tables.utils";
import { TestConnectionStatus } from "src/generic/test-connection/test-connection";
import { Tabs } from "./selectTables";
import utils from "core/utils";
import helpLinkProvider from "app/components/common/helpable/helpLinkProvider";

interface ContentTxtInterface {
    handleExcludeTables: (value: string) => void;
    name: string;
    onIncludeTablesChange: (value: string) => void;
    includeTables: string;
    excludeTables?: string;
    hasExcludeTables?: boolean;
    entityType: string;
    tablesError?: string;
    enableAction: boolean;
    previousTab: Tabs | undefined;
    onSwitchToListMode: () => void;
    onCancellingModeSwitch: () => void;
    adapterName?: string;
}

const ContentTxt: React.FC<ContentTxtInterface> = ({
    handleExcludeTables,
    name,
    onIncludeTablesChange,
    includeTables,
    excludeTables,
    hasExcludeTables = false,
    entityType,
    tablesError = "",
    enableAction,
    previousTab,
    onSwitchToListMode,
    onCancellingModeSwitch,
    adapterName
}) => {
    const viewDocumentationLink =
        (adapterName && helpLinkProvider.getHelpLinkByKey(`PROPERTYTEMPLATE_${adapterName}`)) ||
        helpLinkProvider.getHelpLinkByKey("APPS-ADAPTER-READERS-DEFAULT");
    const { currentSelection, setCurrentSelection } = useSelectTables();
    const [showFullScreenModal, setShowFullScreenModal] = useState<boolean>(false);
    const [tablesInputOnFullScreen, setTablesInputOnFullScreen] = useState<string>(includeTables);
    const [excludeTablesInputOnFullScreen, setExcludeTablesInputOnFullScreen] = useState<string>(excludeTables || "");
    const [fullScreenTabIndex, setFullScreenTabIndex] = useState(0);

    const isInputDisabled = !!currentSelection.schemas || !!currentSelection.queries?.length;

    return (
        <Grid container>
            {isInputDisabled && previousTab === Tabs.query ? null : (
                <>
                    <Grid item>
                        <StriimTypography variant="body4" color={"greyscale.800"} sx={styles.txtSqlDescription}>
                            {tablesInputHelperText}
                            <StriimLink
                                href={viewDocumentationLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<SvgIcon component={NewTab} sx={styles.newTabIcon} />}
                                sx={styles.link}
                            >
                                <StriimTypography variant="caption3">View Documentation</StriimTypography>
                            </StriimLink>
                        </StriimTypography>
                    </Grid>

                    <Grid item xs={12} sx={styles.txtIncludeInput}>
                        <StriimInputField
                            label="Include"
                            name={name}
                            required
                            error={!!tablesError}
                            helperText={tablesError}
                            maxRows={3}
                            fullWidth
                            multiline
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" sx={styles.adornment}>
                                        <Box
                                            component="span"
                                            sx={[
                                                styles.queryInputExpandIcon,
                                                (isInputDisabled || !enableAction) && styles.queryInputDisabledIcon
                                            ]}
                                            onClick={() => {
                                                setFullScreenTabIndex(0);
                                                setTablesInputOnFullScreen(includeTables || "");
                                                setExcludeTablesInputOnFullScreen(excludeTables || "");
                                                setShowFullScreenModal(true);
                                            }}
                                        >
                                            <Expand />
                                        </Box>
                                    </InputAdornment>
                                ),
                                "data-test-id": "form-control-Tables"
                            }}
                            value={includeTables}
                            disabled={isInputDisabled || !enableAction}
                            onChange={value => {
                                onIncludeTablesChange(value);
                            }}
                            isFormElement
                        />
                        {includeTables && (
                            <Box
                                sx={[
                                    styles.clearIconWrapper,
                                    (isInputDisabled || !enableAction) && styles.queryInputDisabledIcon
                                ]}
                                onClick={() => {
                                    onIncludeTablesChange("");
                                }}
                            >
                                <SvgIcon component={Close} sx={styles.clearSvgIcon} />
                            </Box>
                        )}
                    </Grid>

                    {hasExcludeTables && (
                        <Grid item xs={12} sx={styles.txtExcludeInput}>
                            <StriimInputField
                                label="Exclude"
                                name="ExcludedTables"
                                disabled={isInputDisabled || !enableAction}
                                multiline
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end" sx={styles.adornment}>
                                            <Box
                                                component="span"
                                                sx={[
                                                    styles.queryInputExpandIcon,
                                                    (isInputDisabled || !enableAction) &&
                                                        styles.queryInputDisabledIcon
                                                ]}
                                                onClick={() => {
                                                    setFullScreenTabIndex(1);
                                                    setTablesInputOnFullScreen(includeTables);
                                                    setExcludeTablesInputOnFullScreen(excludeTables || "");
                                                    setShowFullScreenModal(true);
                                                }}
                                            >
                                                <Expand />
                                            </Box>
                                        </InputAdornment>
                                    )
                                }}
                                value={excludeTables}
                                onChange={value => {
                                    handleExcludeTables(value);
                                }}
                            />
                            {excludeTables && (
                                <Box
                                    sx={[
                                        styles.clearIconWrapper,
                                        (isInputDisabled || !enableAction) && styles.queryInputDisabledIcon
                                    ]}
                                    onClick={() => {
                                        handleExcludeTables("");
                                    }}
                                >
                                    <SvgIcon component={Close} sx={styles.clearSvgIcon} />
                                </Box>
                            )}
                        </Grid>
                    )}
                </>
            )}

            {isInputDisabled && enableAction && (
                <StriimFeedbackBlock
                    variant="info"
                    content={
                        <Grid container gap={1}>
                            <StriimTypography variant="body4" color="greyscale.900">
                                {previousTab === Tabs.visual
                                    ? `You selected ${entityType.toLowerCase()}s in visual mode. If you edit them in list mode, you can return to visual mode only after resetting the selections.`
                                    : `You have already selected some ${entityType.toLowerCase()}s using a query. If you switch to List mode, you will lose your previous selections.`}
                            </StriimTypography>
                        </Grid>
                    }
                    footer={
                        <Grid container>
                            <Grid item>
                                <StriimButton
                                    variant="text"
                                    sx={styles.footerButton}
                                    onClick={onSwitchToListMode}
                                    data-testid="switch-to-list-mode-button"
                                >
                                    Edit in List Mode
                                </StriimButton>
                            </Grid>
                            <Grid item>
                                <StriimButton
                                    variant="text"
                                    sx={[styles.footerButton, styles.cancelButton]}
                                    onClick={onCancellingModeSwitch}
                                >
                                    Cancel
                                </StriimButton>
                            </Grid>
                        </Grid>
                    }
                    customStyles={styles.customBlock}
                />
            )}
            {showFullScreenModal && (
                <FullScreenInputModal
                    InputComponent={
                        <>
                            <StriimTabs
                                value={fullScreenTabIndex}
                                onChange={(e, newValue) => setFullScreenTabIndex(newValue)}
                                content={
                                    <Box>
                                        <StriimTypography variant="h2" color="primary.700">
                                            {`List the ${utils.capitalize(entityType)}s to ${
                                                fullScreenTabIndex === 0 ? "Include" : "Exclude"
                                            }`}
                                        </StriimTypography>
                                        <StriimTypography
                                            variant="caption3"
                                            fontFamily={"Inter"}
                                            color={"greyscale.600"}
                                        >
                                            {tablesInputHelperText}
                                            <StriimLink
                                                href={viewDocumentationLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                endIcon={
                                                    // @ts-ignore
                                                    <SvgIcon component={NewTab} sx={styles.newTabIcon} />
                                                }
                                            >
                                                <StriimTypography color="secondary.500" variant="caption3">
                                                    View Documentation
                                                </StriimTypography>
                                            </StriimLink>
                                        </StriimTypography>
                                        <Grid item sx={styles.fullScreenInput}>
                                            {fullScreenTabIndex === 0 ? (
                                                <StriimInputField
                                                    name={name}
                                                    multiline
                                                    maxRows={3}
                                                    fullWidth
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end" sx={styles.adornment}>
                                                                <Box
                                                                    component="span"
                                                                    sx={styles.queryInputExpandIcon}
                                                                    onClick={() => {
                                                                        setShowFullScreenModal(false);
                                                                    }}
                                                                >
                                                                    <Minimize />
                                                                </Box>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    value={tablesInputOnFullScreen}
                                                    onChange={value => {
                                                        setTablesInputOnFullScreen(value);
                                                    }}
                                                    required
                                                />
                                            ) : (
                                                <StriimInputField
                                                    name="ExcludedTables"
                                                    multiline
                                                    maxRows={3}
                                                    fullWidth
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end" sx={styles.adornment}>
                                                                <Box
                                                                    component="span"
                                                                    sx={styles.queryInputExpandIcon}
                                                                    onClick={() => {
                                                                        setShowFullScreenModal(false);
                                                                    }}
                                                                >
                                                                    <Minimize />
                                                                </Box>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    value={excludeTablesInputOnFullScreen}
                                                    onChange={value => {
                                                        setExcludeTablesInputOnFullScreen(value);
                                                    }}
                                                />
                                            )}
                                        </Grid>
                                    </Box>
                                }
                            >
                                <StriimTab label="Include" />
                                {hasExcludeTables && <StriimTab label="Exclude" />}
                            </StriimTabs>
                        </>
                    }
                    onCancel={() => {
                        setTablesInputOnFullScreen(includeTables);
                        setExcludeTablesInputOnFullScreen(excludeTables || "");
                        setShowFullScreenModal(false);
                    }}
                    onConfirm={() => {
                        onIncludeTablesChange(tablesInputOnFullScreen);
                        handleExcludeTables(excludeTablesInputOnFullScreen);
                        setShowFullScreenModal(false);
                    }}
                />
            )}
        </Grid>
    );
};

export default ContentTxt;
